"use client";

import { parseAiffBuffer } from "@/utils/audio/aiff/parseAiff";

// Convert AIFF file to WAV using manual parsing Supports 8, 16, 24, and 32-bit AIFF files Uses triangular dithering to prevent quantization artifacts when converting to 16-bit
export const convertAiffToWav = async (aiffFile: File): Promise<File> => {
  try {
    const arrayBuffer = await aiffFile.arrayBuffer();
    const aiffData = parseAiffBuffer(arrayBuffer);

    // Convert to WAV format with proper dithering
    const wavBuffer = aiffDataToWav(aiffData);
    const wavBlob = new Blob([wavBuffer], { type: "audio/wav" });

    // Create new file with WAV extension
    const processedFile = new File(
      [wavBlob],
      aiffFile.name.replace(/\.(aiff|aif)$/i, ".wav"),
      {
        type: "audio/wav",
      }
    );
    return processedFile;
  } catch (error) {
    console.warn("🎵 AIFF conversion failed:", error);
    throw new Error("AIFF conversion failed");
  }
};

// Convert AIFF audio data to WAV format Uses triangular dithering when converting to 16-bit to minimize artifacts
const aiffDataToWav = (aiffData: {
  samples: Float32Array;
  sampleRate: number;
  numChannels: number;
  bitsPerSample: number;
  compressionType?: string;
}): ArrayBuffer => {
  const { samples, sampleRate, numChannels, bitsPerSample } = aiffData;
  const bytesPerSample = 2; // 16-bit output
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const bufferSize = 44 + dataSize;

  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);

  // Helper to write strings
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Write WAV header
  writeString(0, "RIFF");
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // 16 bits per sample
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  // Convert float samples to 16-bit PCM Only apply dithering when downsampling from higher bit depths
  const shouldDither = bitsPerSample > 16;

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    // Clamp to valid range
    let sample = Math.max(-1, Math.min(1, samples[i]));

    // Scale to 16-bit range
    sample = sample * 32767;

    // Add triangular dither only when downsampling to reduce quantization noise
    if (shouldDither) {
      const dither = generateDither();
      sample = sample + dither;
    }

    // Round and clamp to 16-bit integer range
    const intSample = Math.max(-32768, Math.min(32767, Math.round(sample)));

    view.setInt16(offset, intSample, true);
    offset += 2;
  }

  return buffer;
};

// Generate triangular dither noise TPDF (Triangular Probability Density Function) dither reduces quantization artifacts
const generateDither = (): number => {
  // Generate two random numbers and average them for triangular distribution
  return (Math.random() - 0.5 + (Math.random() - 0.5)) * 0.5;
};
